import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  CalendarCount,
  CalendarDetail,
  FavoriteItemListReqDto,
  Member,
  MemberSearchDto,
  MyTaskDashboard,
  NotificationDTO,
  Policy,
  totalItem,
  TreeDataRequest,
  TreeDataResponse,
  TreeNode,
  UserSimpleInfo,
  Workgroup,
  WorkgroupDetailDTO,
  WorkgroupHomeDashboard,
  WorkgroupRespDto,
  WorkgroupSearchDto,
  WorkgroupWithPermissionPolicyTarget,
} from "../../types/workgroup";
import workgroupApi from "../api/WorkgroupApi";
import { meetingDTO, ZoomRespondDto } from "../../types/meeting";
import {
  getUserInfoFromLocalStorage,
  setUserInfoFromLocalStorage,
} from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";

interface WorkgroupState {
  simpleWorkgroupList: Workgroup[];
  workgroupList: Workgroup[];
  workgroupListInfo?: WorkgroupRespDto;
  workgroupSidemenuList?: Workgroup[];
  workgroupNestedSidemenuList: Workgroup[];
  favoriteItems: totalItem[];
  externalUserList?: Member[];
  searchUserResult: MemberSearchDto;
  workgroupDetail?: WorkgroupDetailDTO;
  memberProfile?: {
    userId: string;
    userInfo: Member;
  }[];
  favoriteMeetingInfo?: ZoomRespondDto;
  favoriteMeetingList?: meetingDTO[];
  treeData?: TreeDataResponse;
  workgroupHomeDashboard?: WorkgroupHomeDashboard;
  myTasks?: MyTaskDashboard;
  suggestions?: MyTaskDashboard;
  calendarCount?: CalendarCount[];
  calendarDetail?: CalendarDetail;
  recentHistoryWorkgroup?: Workgroup[];
  savedSidemenuFilter?: WorkgroupSearchDto;
}
// # initial state
const initialState: WorkgroupState = {
  simpleWorkgroupList: [],
  workgroupList: [],
  workgroupSidemenuList: undefined,
  favoriteItems: [],
  externalUserList: [],
  favoriteMeetingList: [],
  workgroupNestedSidemenuList: [],
  searchUserResult: {
    userList: [],
    internalPos: -1,
    externalPos: -1,
    searchOpt: "",
  },
  workgroupListInfo: undefined,
  workgroupDetail: undefined,
  memberProfile: undefined,
  treeData: undefined,
  savedSidemenuFilter: {
    isDetail: "Y",
    managerId: "",
    orderBy: "workgroupName",
    orderByType: "ASC",
    pageRange: 1,
    posPage: 1,
    rowRange: 0,
    searchDate: {
      start: 0,
      end: 0,
    },
    status: 1,
    workgroupName: "",
  },
};

export const trunkGetWorkgroupSimpleList = createAsyncThunk(
  "workgroup/list/simple",
  async (req: WorkgroupSearchDto) => {
    const user = getUserInfoFromLocalStorage() as string | "";
    const loginDto: LoginDto = JSON.parse(user);

    if (loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE") return;

    const resp = await workgroupApi.getWorkgroupSimpleList(req);
    return resp;
  }
);
export const trunkGetMemberList = createAsyncThunk(
  "workgroup/list/searchUser",
  async (req: MemberSearchDto) => {
    const resp = await workgroupApi.getMemberList(req);
    return resp;
  }
);
export const trunkGetWorkgroupFullList = createAsyncThunk(
  "workgroup/list/full",
  async (req: WorkgroupSearchDto) => {
    const user = getUserInfoFromLocalStorage() as string | "";
    const loginDto: LoginDto = JSON.parse(user);

    if (loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE") return;

    const resp = await workgroupApi.getWorkgroupFullList(req);
    return resp;
  }
);
export const trunkSetMeetingFavorite = createAsyncThunk(
  "workgroup/favorite/set",
  async (req: { meetingId: string; workgroupId: string; flag: boolean }) => {
    return req;
  }
);
export const trunkGetTotalFavoriteItems = createAsyncThunk(
  "workgroup/favorite",
  async (req: FavoriteItemListReqDto) => {
    const user = getUserInfoFromLocalStorage() as string | "";
    const loginDto: LoginDto = JSON.parse(user);

    if (loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE") return;

    const resp = await workgroupApi.getTotalFavoriteItems(req);
    return resp;
  }
);
export const trunkGetWorkgroupDetail = createAsyncThunk(
  "workgroup/detail",
  async (req: { workgroupId: string }) => {
    const resp = await workgroupApi.getWorkgroupDetail(req);
    return resp;
  }
);
export const thrunkWorkgroupFavorite = createAsyncThunk(
  "workgroup/favorite/update",
  async (req: {
    workgroupId: string;
    users: {
      userId: string;
      favorite: string;
    }[];
  }) => {
    const resp = await workgroupApi.doWorkgroupFavorite(
      req.workgroupId,
      req.users
    );
    return resp;
  }
);
export const trunkWorkgroupSideList = createAsyncThunk(
  "workgroup/list/side",
  async (reqDTO: WorkgroupSearchDto) => {
    const user = getUserInfoFromLocalStorage() as string | "";
    const loginDto: LoginDto = JSON.parse(user);

    if (loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE") return;

    const workgroupList = await workgroupApi.getWorkgroupSideList(reqDTO);
    return workgroupList;
  }
);
export const trunkOrgansTree = createAsyncThunk(
  "workgroup/organs/tree",
  async (treeDataRequest: TreeDataRequest) => {
    const treeData = await workgroupApi.getOrgansTree(treeDataRequest);
    return treeData;
  }
);
export const trunkUpdateStarItemFavorite = createAsyncThunk(
  "workgroup/favorite/update/content",
  async (req: { syncIds: string[]; flag: boolean }) => {
    return req;
  }
);
export const thrunkMembersProfile = createAsyncThunk(
  "members/profile",
  async (userId: string) => {
    return await workgroupApi.membersProfile(userId);
  }
);
export const thrunkMemberChangeInfo = createAsyncThunk(
  "members/update",
  async (users: UserSimpleInfo[]) => {
    return await workgroupApi.workgroupChangeInfo(users);
  }
);
export const trunkFavoriteMeetingList = createAsyncThunk(
  "favorite/list/meeting",
  async (req: FavoriteItemListReqDto) => {
    return await workgroupApi.getFavoriteMeetingList(req);
  }
);
export const trunkGetWorkspaceConfig = createAsyncThunk(
  "workgroup/config",
  async () => {
    return await workgroupApi.getWorkspaceConfig();
  }
);
export const trunkWorkgroupCreate = createAsyncThunk(
  "workgroup/insert",
  async (workgroupRequest: WorkgroupWithPermissionPolicyTarget) => {
    const workgroup = await workgroupApi.createNewWorkgroup(workgroupRequest);
    return workgroup;
  }
);
export const trunkCreateNewPrivateWorkgroups = createAsyncThunk(
  "workgroup/insert/private",
  async (workgroupRequest: WorkgroupWithPermissionPolicyTarget) => {
    const workgroup = await workgroupApi.createNewPrivateWorkgroups(
      workgroupRequest
    );
    return workgroup;
  }
);
export const trunkResendInviteMail = createAsyncThunk(
  "workgroups/members/resend/invitemail",
  async ({ workgroupId, userId }: { workgroupId: string; userId: string }) => {
    const members = await workgroupApi.resendInviteMail(workgroupId, userId);
    return members;
  }
);
export const trunkWorkgroupHomeDashboard = createAsyncThunk(
  "workgroup/home/dashboard",
  async (workgrupId: string) => {
    const workgroupHomeDashboard = await workgroupApi.workgroupHomeDashboard(
      workgrupId
    );
    return workgroupHomeDashboard;
  }
);
export const trunkUpdateWorkgroupInfo = createAsyncThunk(
  "workgroup/update",
  async (req: {
    workgroupId: string;
    managerId?: string;
    workgroup?: Workgroup;
    policy?: Policy;
  }) => {
    return await workgroupApi.updateWorkgroupInfo(req);
  }
);
let findNode = false;
const checkSubTree = (item: TreeNode, treeData: TreeDataResponse): void => {
  if (findNode) {
    return;
  }
  if (
    item.type === "dept" &&
    item.value === treeData.path &&
    item.hasChildren
  ) {
    if (item.subTree.length === 0) {
      item.subTree = treeData.nodes;
    }
    findNode = true;
  } else if (
    item.type === "dept" &&
    item.hasChildren &&
    item.subTree.length > 0
  ) {
    item.subTree.forEach((subItem) => {
      checkSubTree(subItem, treeData);
    });
  }
};
export const trunGetWorkgroupNoti = createAsyncThunk(
  "workgroups/notification/get",
  async (workgroupId: string) => {
    const members = await workgroupApi.getWorkgroupNoti(workgroupId);
    return members;
  }
);
export const trunkUpdateWorkgroupNoti = createAsyncThunk(
  "workgroups/notification/update",
  async ({
    workgroupId,
    req,
  }: {
    workgroupId: string;
    req: NotificationDTO;
  }) => {
    const members = await workgroupApi.updateWorkgroupNoti(workgroupId, req);
    return members;
  }
);
export const thurnkWorkgroupUpdateStatus = createAsyncThunk(
  "workgroup/update/status",
  async ({ workgroupId, status }: { workgroupId: string; status: number }) => {
    return await workgroupApi.workgroupUpdateStatus(workgroupId, status);
  }
);
export const thrunkMemberWithdraw = createAsyncThunk(
  "members/withdarw",
  async ({
    workgroupId,
    users,
  }: {
    workgroupId: string;
    users: {
      userId: string;
      userType: number;
      withdrawYN: string;
    }[];
  }) => {
    return await workgroupApi.workgroupmemberWithdraw(workgroupId, users);
  }
);
export const trunkGetMyDashboardSuggestion = createAsyncThunk(
  "home/suggestions",
  async () => {
    return await workgroupApi.getMyDashboardSuggestion();
  }
);
export const trunkGetWorkgroupCalendarCount = createAsyncThunk(
  "home/calendar/count",
  async (req: {
    workgroupId: string;
    date: number;
    startDate?: number;
    endDate?: number;
    offset: number;
  }) => {
    return await workgroupApi.getWorkgroupCalendarCount(req);
  }
);
export const trunkGetWorkgroupCalendarDetail = createAsyncThunk(
  "home/calendar/detail",
  async (req: { workgroupId: string; date: number }) => {
    return await workgroupApi.getWorkgroupCalendarDetail(req);
  }
);
export const trunkGetMyDashboardTasks = createAsyncThunk(
  "home/tasks",
  async () => {
    return await workgroupApi.getMyDashboardTasks();
  }
);
export const trunkRemoveTaskFromList = createAsyncThunk(
  "home/tasks/remove",
  async (requestId: string) => {
    return requestId;
  }
);

export const trunkGetRecentHistoryWorkgroupList = createAsyncThunk(
  "home/recent",
  async (req: { startDate: number; endDate: number }) => {
    const user = getUserInfoFromLocalStorage() as string | "";
    const loginDto: LoginDto = JSON.parse(user);

    if (loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE") return;

    return await workgroupApi.getRecentHistoryWorkgroupList(req);
  }
);

export const trunkDeleteMember = createAsyncThunk(
  "user/delete",
  async (userId: string) => {
    return await workgroupApi.deleteMember(userId);
  }
);
export const trunkCheckCheckoutUser = createAsyncThunk(
  "user/check/checkOut",
  async (req: { userIds: string[]; workgroupId?: string }) => {
    return await workgroupApi.checkCheckoutUser(req);
  }
);
export const trunkMoveWorkgroup = createAsyncThunk(
  "workgroup/move",
  async (req: { workgroupId: string; targetParentWorkgroupId: string }) => {
    return await workgroupApi.moveWorkgroup(req);
  }
);
const shuffleArray = (array?: any[]) => {
  if (!array) return [];
  const result = [...array]; // 원본 배열 손상 방지
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]]; // swap
  }
  return result;
};
const makeNestedWorkgroupList = (recievedWorkgroupList: Workgroup[]) => {
  const map = new Map();
  recievedWorkgroupList.forEach((item) =>
    map.set(item.workgroupId, { ...item, children: [] })
  );
  const roots: Workgroup[] = [];
  recievedWorkgroupList.forEach((item) => {
    if (
      item.parentWorkgroupId === null ||
      item.rootWorkgroupId === item.workgroupId
    ) {
      roots.push(map.get(item.workgroupId));
    } else {
      const parent = map.get(item.parentWorkgroupId);
      if (parent) {
        parent.children.push(map.get(item.workgroupId));
      }
    }
  });
  return roots;
};
const workgroupSlice = createSlice({
  name: "workgroup",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      trunkGetWorkgroupSimpleList.fulfilled,
      (state, { payload: data }) => {
        if (data) state.simpleWorkgroupList = data.workgroupList;
      }
    );
    builder.addCase(thrunkMemberWithdraw.fulfilled, (state, arg) => {
      state.workgroupList = state.workgroupList.filter(
        (item) => item.workgroupId !== arg.meta.arg.workgroupId
      );
      if (state.workgroupListInfo)
        state.workgroupListInfo = {
          ...state.workgroupListInfo,
          total: state?.workgroupListInfo?.total
            ? state?.workgroupListInfo?.total - 1
            : 0,
          workgroupList:
            state.workgroupListInfo?.workgroupList?.filter(
              (item) => item.workgroupId !== arg.meta.arg.workgroupId
            ) ?? [],
        };
      state.workgroupSidemenuList = state.workgroupSidemenuList?.filter(
        (item) => item.workgroupId !== arg.meta.arg.workgroupId
      );
    });
    builder.addCase(trunkGetMemberList.fulfilled, (state, action) => {
      if (action.meta.arg.type === "external") {
        if (window.eco.serviceType === "OCI") {
          state.externalUserList = action.payload as any[];
        } else if (action.meta.arg.externalPos === 1)
          state.externalUserList = action.payload.userList;
        else
          state.externalUserList = [
            ...(state.externalUserList ?? []),
            ...(action.payload.userList ?? []),
          ];
      }
      state.searchUserResult =
        window.eco.serviceType === "OCI"
          ? {
              searchOpt: action.meta.arg.searchOpt,
              userList: [...((action.payload as any[]) ?? [])],
            }
          : {
              ...action.payload,
              userList: !(
                action.meta.arg.internalPos === 1 &&
                action.meta.arg.externalPos === 1
              )
                ? [
                    ...(state.searchUserResult.userList ?? []),
                    ...(action.payload.userList ?? []),
                  ]
                : [...(action.payload.userList ?? [])],
            };
    });
    builder.addCase(trunkGetWorkgroupFullList.fulfilled, (state, arg) => {
      if (!arg.payload) return;

      if (arg.meta.arg.posPage === 1) {
        state.workgroupList = arg.payload.workgroupList;
        state.workgroupListInfo = arg.payload;
      } else {
        state.workgroupList = [
          ...state.workgroupList,
          ...arg.payload.workgroupList,
        ];
        if (state.workgroupListInfo)
          state.workgroupListInfo.workgroupList = [
            ...state.workgroupListInfo.workgroupList,
            ...arg.payload.workgroupList,
          ];
      }
    });
    builder.addCase(trunkWorkgroupCreate.fulfilled, (state, arg) => {
      state.workgroupList = [...state.workgroupList, arg.payload];
      makeNestedWorkgroupList([
        ...(state.workgroupSidemenuList ?? []),
        arg.payload,
      ]);
      state.workgroupSidemenuList = [
        ...(state.workgroupSidemenuList ?? []),
        arg.payload,
      ];

      if (state.workgroupListInfo?.workgroupList)
        state.workgroupListInfo.workgroupList = [
          ...state.workgroupListInfo.workgroupList,
          arg.payload,
        ];
    });
    builder.addCase(trunkCreateNewPrivateWorkgroups.fulfilled, (state, arg) => {
      state.workgroupList = [...state.workgroupList, arg.payload];
      makeNestedWorkgroupList([
        ...(state.workgroupSidemenuList ?? []),
        arg.payload,
      ]);
      state.workgroupSidemenuList = [
        ...(state.workgroupSidemenuList ?? []),
        arg.payload,
      ];

      if (state.workgroupListInfo?.workgroupList)
        state.workgroupListInfo.workgroupList = [
          ...state.workgroupListInfo.workgroupList,
          arg.payload,
        ];
    });
    builder.addCase(trunkWorkgroupSideList.fulfilled, (state, arg) => {
      if (!arg.payload) return;
      if (!arg.meta.arg.noSaveConfig) state.savedSidemenuFilter = arg.meta.arg;
      // state.workgroupSidemenuList = arg.payload.workgroupList.filter(
      //   (item) => item.workgroupJoined !== false
      // );
      state.workgroupSidemenuList = arg.payload.workgroupList;
      state.workgroupNestedSidemenuList = makeNestedWorkgroupList(
        arg.payload.workgroupList
      );
    });
    builder.addCase(
      trunkWorkgroupHomeDashboard.fulfilled,
      (state, { payload: workgroupHomeDashboard }) => {
        state.workgroupHomeDashboard = workgroupHomeDashboard;
      }
    );
    builder.addCase(
      trunkGetWorkspaceConfig.fulfilled,
      (state, { payload: config }) => {
        const defaultConfig = localStorage.getItem("fasoo-eco-user");
        if (!defaultConfig) return;
        let defaultConfigJson = JSON.parse(defaultConfig);
        defaultConfigJson = {
          ...defaultConfigJson,
          serviceConfig: {
            ...defaultConfigJson.serviceConfig,
            chatAvailibility: config.chatAvailibility ?? false,
            companyName: config.companyName ?? "",
          },
          ecoCommonConfig: {
            ...config,
            workgroupPermissionDTO: {
              ...config.workgroupPermissionDTO,
              policy: {
                ...config?.workgroupPermissionDTO?.policy,
                drmPerm: config?.workgroupPermissionDTO?.policy?.drmPerm ?? [],
              },
            },
          },
        };
        localStorage.setItem(
          "fasoo-eco-user",
          JSON.stringify(defaultConfigJson)
        );
      }
    );
    builder.addCase(
      trunkOrgansTree.fulfilled,
      (state, { payload: treeData }) => {
        if (window.eco.serviceType === "OCI") {
          state.treeData = {
            version: "1",
            path: "ae4fc0f5fd8f4142834c1ad4e929a514",
            nodes: (treeData as unknown as any[]).map((item) => {
              return {
                columnText: item.userName,
                subTree: treeData as any,
                showChildren: true,
                hasChildren: false,
                value: item.userId,
                type: "user",
                isPolicySet: false,
              };
            }),
          };
        } else if (treeData.path === "ae4fc0f5fd8f4142834c1ad4e929a514") {
          state.treeData = treeData;
        } else {
          findNode = false;
          state.treeData?.nodes.forEach((item) => {
            checkSubTree(item, treeData);
          });
        }
      }
    );
    builder.addCase(
      trunkSetMeetingFavorite.fulfilled,
      (state, { payload: data }) => {
        state.favoriteMeetingList = state.favoriteMeetingList?.filter(
          (item) => item.meetingId + item.occurrenceId !== data.meetingId
        );
        if (!state.favoriteMeetingInfo) return;

        state.favoriteMeetingInfo = {
          ...state.favoriteMeetingInfo,
          meetingInfoList:
            state.favoriteMeetingInfo?.meetingInfoList?.filter(
              (item) => item.meetingId + item.occurrenceId !== data.meetingId
            ) ?? [],
          total: (state.favoriteMeetingInfo.total -= 1),
        };
      }
    );
    builder.addCase(
      trunkGetTotalFavoriteItems.fulfilled,
      (state, { payload: data }) => {
        state.favoriteItems = data?.favoriteTotalItems ?? [];
      }
    );
    builder.addCase(trunkGetTotalFavoriteItems.rejected, (state) => {
      state.favoriteItems = [];
    });
    builder.addCase(
      trunkUpdateStarItemFavorite.fulfilled,
      (state, { payload: data }) => {
        state.favoriteItems = state.favoriteItems.map((item) => {
          return {
            ...item,
            content: item.content
              ? {
                  ...item.content,
                  favoritDate: data.syncIds.includes(item.content.syncId)
                    ? data.flag
                      ? 1
                      : 0
                    : item.content.favoritDate,
                }
              : null,
          };
        });
      }
    );
    builder.addCase(
      trunkMoveWorkgroup.fulfilled,
      (state, { payload: data }) => {
        if (state.workgroupDetail?.workgroup) {
          state.workgroupDetail.workgroup.parentWorkgroupId =
            data.parentWorkgroupId;
          state.workgroupDetail.workgroup.rootWorkgroupId =
            data.rootWorkgroupId;
        }
      }
    );
    builder.addCase(
      trunkGetWorkgroupDetail.fulfilled,
      (state, { payload: data }) => {
        state.workgroupDetail = data;
      }
    );
    builder.addCase(trunkUpdateWorkgroupInfo.fulfilled, (state, arg) => {
      if (!state.workgroupDetail?.workgroup) return;
      let newWorkgroupDetail = { ...state.workgroupDetail };

      if (arg.meta.arg.policy && newWorkgroupDetail?.policy) {
        newWorkgroupDetail.policy = {
          ...newWorkgroupDetail.policy,
          ...arg.meta.arg.policy,
          changeCode: newWorkgroupDetail?.policy?.changeCode ?? "",
          uniqueId: newWorkgroupDetail?.policy?.uniqueId ?? "",
          policyCode: newWorkgroupDetail?.policy?.policyCode ?? "",
          updateUser: newWorkgroupDetail?.policy?.updateUser ?? "",
        };
      }
      if (arg.meta.arg.workgroup && newWorkgroupDetail?.workgroup) {
        newWorkgroupDetail.workgroup = arg.meta.arg.workgroup;
      }
      if (arg.meta.arg.workgroup) {
        let newMembers = state.workgroupDetail.workgroup.members.filter(
          (item) =>
            !(
              arg.meta.arg.workgroup?.members[0].changeCode === "DELETE" &&
              item.userId === arg.meta.arg.workgroup?.members[0].userId
            )
        );
        newWorkgroupDetail.workgroup = {
          ...arg.meta.arg.workgroup,
          members: newMembers,
        };
      } else if (arg.meta.arg.managerId) {
        newWorkgroupDetail.workgroup = {
          ...newWorkgroupDetail.workgroup,
          managerId: arg.meta.arg.managerId,
        };
      }
      if (arg.meta.arg.workgroup) {
        if (state.workgroupList)
          state.workgroupList = state.workgroupList.map((item) =>
            item.workgroupId === arg.meta.arg.workgroupId
              ? arg.meta.arg.workgroup ?? item
              : item
          );
        if (state.workgroupSidemenuList)
          state.workgroupSidemenuList = state.workgroupSidemenuList.map(
            (item) =>
              item.workgroupId === arg.meta.arg.workgroupId
                ? arg.meta.arg.workgroup ?? item
                : item
          );
      }

      state.workgroupDetail = {
        workgroup: newWorkgroupDetail.workgroup,
        policy: newWorkgroupDetail.policy,
      };
    });
    builder.addCase(
      thurnkWorkgroupUpdateStatus.fulfilled,
      (state, { payload: workgroup }) => {
        state.workgroupList = workgroup;
        state.workgroupSidemenuList = workgroup;
      }
    );
    builder.addCase(thrunkWorkgroupFavorite.fulfilled, (state, arg) => {
      const workgroupList = arg.payload;
      // state.workgroupList = workgroupList;
      state.workgroupSidemenuList = workgroupList;
      if (state.workgroupList) {
        state.workgroupList = state.workgroupList.map((item) =>
          item.workgroupId === arg.meta.arg.workgroupId
            ? { ...item, favorite: arg.meta.arg.users[0].favorite }
            : item
        );
      }
      if (state.workgroupDetail) {
        state.workgroupDetail.workgroup.favorite =
          state.workgroupDetail.workgroup.favorite === "N" ? "Y" : "N";
      }
    });
    builder.addCase(
      thrunkMembersProfile.fulfilled,
      (state, { payload: member }) => {
        if (
          state.memberProfile === undefined ||
          state.memberProfile[0].userId === member.userId
        ) {
          state.memberProfile = [
            {
              userId: member.userId,
              userInfo: member,
            },
          ];
          const user = getUserInfoFromLocalStorage() as string | "";
          const loginDto: LoginDto = JSON.parse(user);

          setUserInfoFromLocalStorage({
            ...loginDto,
            userInfo: {
              ...loginDto.userInfo,
              userName: member.userName ?? "",
              deptName: member.deptName ?? loginDto.userInfo.deptName ?? "",
              deptCode: member.deptCode ?? loginDto.userInfo.deptCode ?? "",
            },
            ecoUserInfo: {
              ...loginDto.ecoUserInfo,
              status: member.status,
              userType: member.userType,
              linkedYN: member.linkedYN ?? loginDto.ecoUserInfo.linkedYN ?? "N",
              deptName: member.deptName ?? loginDto.ecoUserInfo.deptName ?? "",
            },
          });
        } else {
          state.memberProfile.push({
            userId: member.userId,
            userInfo: member,
          });
        }
      }
    );
    builder.addCase(
      trunkFavoriteMeetingList.fulfilled,
      (state, { payload: favoriteMeetingList }) => {
        if (favoriteMeetingList.pageNo <= 1) {
          state.favoriteMeetingList = favoriteMeetingList.meetingInfoList;
          state.favoriteMeetingInfo = favoriteMeetingList;
        } else {
          if (state.favoriteMeetingInfo)
            state.favoriteMeetingInfo.meetingInfoList = [
              ...(state.favoriteMeetingList ?? []),
              ...(favoriteMeetingList?.meetingInfoList ?? []),
            ];
          state.favoriteMeetingList = [
            ...(state.favoriteMeetingList ?? []),
            ...(favoriteMeetingList?.meetingInfoList ?? []),
          ];
        }
      }
    );
    builder.addCase(
      trunkGetMyDashboardTasks.fulfilled,
      (state, { payload: resp }) => {
        state.myTasks = { tasks: shuffleArray(resp.tasks) };
      }
    );
    builder.addCase(trunkRemoveTaskFromList.fulfilled, (state, resp) => {
      if (state.myTasks)
        state.myTasks.tasks = state.myTasks?.tasks.filter(
          (item) => item.data.requestId !== resp.payload
        );
    });
    builder.addCase(
      trunkGetRecentHistoryWorkgroupList.fulfilled,
      (state, { payload: resp }) => {
        state.recentHistoryWorkgroup = resp;
      }
    );
    builder.addCase(
      trunkGetMyDashboardSuggestion.fulfilled,
      (state, { payload: resp }) => {
        state.suggestions = resp;
      }
    );
    builder.addCase(
      trunkGetWorkgroupCalendarCount.fulfilled,
      (state, { payload: resp }) => {
        state.calendarCount = resp;
      }
    );
    builder.addCase(
      trunkGetWorkgroupCalendarDetail.fulfilled,
      (state, { payload: resp }) => {
        state.calendarDetail = {
          ...resp,
          events: {
            signs: resp.events.signs ?? [],
            readRequests: resp.events.readRequests ?? [],
            meetings: resp.events.meetings ?? [],
          },
        };
      }
    );
  },
});
export const authActions = workgroupSlice.actions;

export default workgroupSlice.reducer;
